import anime from 'animejs'
import { useRef } from 'react'

const clampVelocity = (velocity, max) => {
  let magnitude = Math.abs(velocity)
  let direction = Math.sign(velocity)

  return Math.min(magnitude, max) * direction
}

const computeDirection = (startPosition, currentPosition) => {
  let deltaX = currentPosition.x - startPosition.x
  let deltaY = currentPosition.y - startPosition.y

  let distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
  if (distance < 5) return ''

  let angle = Math.abs((Math.atan2(deltaY, deltaX) * 180) / Math.PI)
  return angle < 45 || angle > 135 ? 'horizontal' : 'vertical'
}

const adjustPosition = (position, context) => {
  if (!context.containerRef.current || !context.trayRef.current) return

  let containerWidth = context.containerRef.current.offsetWidth
  let trayWidth = context.trayRef.current.offsetWidth

  if (position > 0 || containerWidth >= trayWidth) {
    let newPosition = 0
    let distance = Math.abs(newPosition - position)

    context.animation = anime({
      targets: context.trayRef.current,
      translateX: 0,
      duration: distance * 1.5,
      easing: 'easeOutCubic',
      complete: () => {
        context.animation = null
        context.setPosition(0)
      },
    })

    return
  }

  if (position < containerWidth - trayWidth) {
    let newPosition = containerWidth - trayWidth
    let distance = Math.abs(newPosition - position)

    context.animation = anime({
      targets: context.trayRef.current,
      translateX: newPosition,
      duration: distance * 1.5,
      easing: 'easeOutCubic',
      complete: () => {
        context.animation = null
        context.setPosition(newPosition)
      },
    })

    return
  }

  context.setPosition(position)
}

const startDrag = (touchPosition, context) => {
  context.start = true
  context.startPosition = context.position
  context.startDirection = ''
  context.touchStartPosition = touchPosition

  context.startX = touchPosition.x
  context.startTime = Date.now()
  context.lastX = context.startX
  context.lastTime = context.startTime
  context.distance = 0
  context.velocity = 0

  if (context.animation) {
    context.animation.pause()
    context.animation = null
    context.startPosition = parseFloat(
      anime.get(context.trayRef.current, 'translateX')
    )
  }
}

const drag = (touchPosition, context) => {
  if (!context.start) return

  if (!context.startDirection) {
    context.startDirection = computeDirection(
      context.touchStartPosition,
      touchPosition
    )
  }

  if (context.startDirection !== 'horizontal') return

  if (context.ticking) return

  context.ticking = true

  requestAnimationFrame(() => {
    let currentTime = Date.now()

    context.distance = touchPosition.x - context.startX
    context.velocity =
      (touchPosition.x - context.lastX) / (currentTime - context.lastTime)

    context.lastX = touchPosition.x
    context.lastTime = currentTime

    anime.set(context.trayRef.current, {
      translateX: context.startPosition + context.distance,
    })

    context.ticking = false
  })
}

const stopDrag = context => {
  if (!context.start) return

  context.start = false

  if (Math.abs(context.distance) < 10) {
    adjustPosition(context.startPosition + context.distance, context)
    return
  }

  if (!context.containerRef.current || !context.trayRef.current) return

  let containerWidth = context.containerRef.current.offsetWidth
  let trayWidth = context.trayRef.current.offsetWidth

  let smoothFactor = 150
  let extraDistance = clampVelocity(context.velocity, 5) * smoothFactor
  let newPosition = context.startPosition + context.distance + extraDistance

  if (newPosition > 0 || containerWidth >= trayWidth) {
    newPosition = 0
  }

  if (newPosition < containerWidth - trayWidth && containerWidth < trayWidth) {
    newPosition = containerWidth - trayWidth
  }

  let distance = Math.abs(
    newPosition - context.startPosition + context.distance
  )

  context.animation = anime({
    targets: context.trayRef.current,
    translateX: newPosition,
    duration: distance * 1.5,
    easing: 'easeOutQuad',
    complete: () => {
      context.animation = null
      adjustPosition(newPosition, context)
    },
  })
}

const useDraggable = (containerRef, trayRef, position, setPosition) => {
  let context = useRef({
    containerRef: null,
    trayRef: null,
    position: 0,
    setPosition: null,
    start: false,
    startPosition: 0,
    startDirection: '',
    touchStartPosition: null,
    startX: 0,
    startTime: 0,
    lastX: 0,
    lastTime: 0,
    distance: 0,
    velocity: 0,
    animation: null,
    ticking: false,
  })

  context.current.containerRef = containerRef
  context.current.trayRef = trayRef
  context.current.position = position
  context.current.setPosition = setPosition

  let eventHandlers = {
    onMouseDown: event => {
      startDrag({ x: event.clientX, y: event.clientY }, context.current)
    },

    onMouseMove: event => {
      drag({ x: event.clientX, y: event.clientY }, context.current)
    },

    onMouseUp: () => {
      stopDrag(context.current)
    },

    onMouseLeave: () => {
      stopDrag(context.current)
    },

    onTouchStart: event => {
      let touch = event.touches[0]
      startDrag({ x: touch.clientX, y: touch.clientY }, context.current)
    },

    onTouchMove: event => {
      let touch = event.touches[0]
      drag({ x: touch.clientX, y: touch.clientY }, context.current)
    },

    onTouchEnd: () => {
      stopDrag(context.current)
    },

    onDragStart: event => {
      event.preventDefault()
    },

    onClickCapture: event => {
      if (context.current.startDirection) {
        event.preventDefault()
        event.stopPropagation()
      }
    },
  }

  return { eventHandlers }
}

export default useDraggable
